/** View and edit inquiry pricing from the terminal assistant. */
import { fmtAmt, syncVisaPricingExtra, syncTourAgentCost, ticketPricingFromExtra } from './utils.js';
import {
  applyPassengerFieldEdit,
  recalcLeadFromExtra,
} from './assistant-edit.js';

function numOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function capturePricingSnapshot(lead = {}) {
  const extra = lead.extra || {};
  const pricing = ticketPricingFromExtra(extra);
  const cost = numOrNull(lead.cost_amount) ?? pricing?.cost ?? null;
  const total = numOrNull(lead.quote_amount) ?? numOrNull(lead.amount) ?? pricing?.total ?? null;
  const profit = pricing?.profit ?? (cost != null && total != null ? Math.round(total - cost) : null);

  const travelers = [];
  if (lead.type === 'ticket') {
    (extra.adt_profiles || []).forEach((pr, i) => {
      const name = String(pr.name || '').trim();
      travelers.push({
        kind: 'adt',
        index: i,
        label: `ADT ${i + 1}${name ? ` — ${name}` : ''}`,
        cost: numOrNull(pr.cost),
        profit: numOrNull(pr.profit),
      });
    });
    if (!travelers.length && (extra.pax?.adt || 0) > 0) {
      const rates = extra.pax_cost_rates || {};
      const profits = extra.pax_profit_rates || {};
      travelers.push({
        kind: 'adt_rate',
        label: `ADT per person (×${extra.pax.adt})`,
        cost: numOrNull(rates.adt),
        profit: numOrNull(profits.adt),
      });
    }
  }

  return {
    type: lead.type || 'ticket',
    cost,
    profit,
    total,
    quote: numOrNull(lead.amount) ?? total,
    travelers,
  };
}

export function previewLeadAfterPricingChanges(lead, changes = {}) {
  if (changes.extra && !changes.recalcPricing) {
    return {
      ...lead,
      extra: changes.extra,
      amount: changes.amount ?? lead.amount,
      cost_amount: changes.cost_amount ?? lead.cost_amount,
      quote_amount: changes.quote_amount ?? lead.quote_amount,
    };
  }
  if (changes.amount !== undefined || (changes.cost_amount !== undefined && !changes.extra)) {
    return {
      ...lead,
      amount: changes.amount ?? lead.amount,
      cost_amount: changes.cost_amount ?? lead.cost_amount,
      quote_amount: changes.quote_amount ?? lead.quote_amount,
    };
  }
  const extra = { ...(lead.extra || {}), ...(changes.extra || {}) };
  const recalc = changes.recalcPricing ? recalcLeadFromExtra(lead, extra) : null;
  return {
    ...lead,
    extra: recalc?.extra ?? extra,
    quote_amount: recalc?.quote_amount ?? changes.quote_amount ?? lead.quote_amount,
    cost_amount: recalc?.cost_amount ?? changes.cost_amount ?? lead.cost_amount,
    amount: changes.amount ?? lead.amount,
  };
}

export function formatPricingSummaryText(lead) {
  const snap = capturePricingSnapshot(lead);
  const lines = ['Pricing'];

  if (snap.type === 'ticket') {
    if (snap.travelers.length) {
      snap.travelers.forEach((t) => {
        const c = t.cost ?? 0;
        const p = t.profit ?? 0;
        lines.push(`  ${t.label}: cost ${fmtAmt(c)} · profit ${fmtAmt(p)} · total ${fmtAmt(c + p)}`);
      });
    } else {
      lines.push('  No per-adult pricing on file.');
    }
    if (snap.cost != null || snap.total != null) {
      lines.push('  ——');
      lines.push(`  Total cost: ${fmtAmt(snap.cost ?? 0)} · profit: ${fmtAmt(snap.profit ?? 0)} · quote: ${fmtAmt(snap.total ?? 0)}`);
    }
    return lines.join('\n');
  }

  lines.push(`  Quote: ${fmtAmt(snap.quote ?? snap.total ?? 0)} · cost: ${fmtAmt(snap.cost ?? 0)}`);
  if (snap.cost != null && (snap.quote ?? snap.total) != null) {
    lines.push(`  Profit: ${fmtAmt((snap.quote ?? snap.total) - snap.cost)}`);
  }
  return lines.join('\n');
}

/** @returns {object[]} pricing edit targets */
export function listPricingTargets(lead) {
  const snap = capturePricingSnapshot(lead);
  if (snap.type !== 'ticket') {
    return [
      { kind: 'simple', id: 'quote', label: 'Quote amount', fieldKey: 'amount' },
      { kind: 'simple', id: 'cost', label: 'Cost amount', fieldKey: 'cost_amount' },
    ];
  }
  return snap.travelers.map((t, i) => ({ ...t, menuIndex: i + 1 }));
}

export function pricingMenuText(lead, { editable = true } = {}) {
  const summary = formatPricingSummaryText(lead);
  const targets = listPricingTargets(lead);
  if (!editable) {
    return `${summary}\n\nView only — type request edit to change pricing.`;
  }
  if (!targets.length) {
    return `${summary}\n\nNo pricing fields to edit.`;
  }
  const lines = [summary, '', 'Edit which?'];
  targets.forEach((t, i) => {
    if (t.kind === 'simple') {
      const val = t.fieldKey === 'amount'
        ? (lead.amount ?? lead.quote_amount)
        : lead.cost_amount;
      lines.push(`  ${i + 1} ${t.label}${val != null ? ` (${fmtAmt(val)})` : ''}`);
    } else {
      const c = t.cost ?? 0;
      const p = t.profit ?? 0;
      lines.push(`  ${i + 1} ${t.label} — cost ${fmtAmt(c)} · profit ${fmtAmt(p)}`);
    }
  });
  lines.push('  done — return to inquiry');
  return lines.join('\n');
}

export function parsePricingPick(raw, lead) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'done' || s === 'back' || s === 'ig') return 'done';
  const targets = listPricingTargets(lead);
  const num = Number(s);
  if (Number.isInteger(num) && num >= 1 && num <= targets.length) {
    return targets[num - 1];
  }
  return null;
}

export function pricingFieldMenuText(target, lead) {
  if (target.kind === 'simple') {
    const val = target.fieldKey === 'amount'
      ? (lead.amount ?? lead.quote_amount)
      : lead.cost_amount;
    return [
      `Edit ${target.label}${val != null ? ` (now: ${fmtAmt(val)})` : ''}?`,
      'Enter amount in LKR.',
      '  back',
    ].join('\n');
  }
  const c = target.cost ?? 0;
  const p = target.profit ?? 0;
  return [
    `Edit ${target.label}`,
    `  Now: cost ${fmtAmt(c)} · profit ${fmtAmt(p)} · total ${fmtAmt(c + p)}`,
    '  1 net fare / cost (LKR)',
    '  2 profit (LKR)',
    '  back',
  ].join('\n');
}

export function parsePricingFieldPick(raw, target) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'back' || s === 'done' || s === 'ig') return 'back';
  if (target.kind === 'simple') return '__value__';
  if (s === '1' || s.includes('cost') || s.includes('net') || s.includes('fare')) return 'cost';
  if (s === '2' || s.includes('profit')) return 'profit';
  return null;
}

export function pricingValuePrompt(target, fieldId, lead) {
  if (target.kind === 'simple') {
    const val = target.fieldKey === 'amount'
      ? (lead.amount ?? lead.quote_amount)
      : lead.cost_amount;
    return {
      question: `New ${target.label}${val != null ? ` (now: ${fmtAmt(val)})` : ''}?`,
      hint: 'Amount in LKR',
    };
  }
  const cur = fieldId === 'cost' ? target.cost : target.profit;
  const label = fieldId === 'cost' ? 'net fare (LKR)' : 'profit (LKR)';
  return {
    question: `New ${label}${cur != null ? ` (now: ${fmtAmt(cur)})` : ''}?`,
    hint: 'Amount in LKR (0 or more)',
  };
}

export function validatePricingValue(target, fieldId, raw) {
  const v = String(raw || '').trim();
  const n = Number(v.replace(/,/g, ''));
  if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR (0 or more).';
  return null;
}

export function parsePricingValue(raw) {
  return Number(String(raw || '').trim().replace(/,/g, ''));
}

/** @returns {object} partial changes for buildLeadUpdateBody */
export function buildPricingFieldChanges(lead, target, fieldId, value) {
  if (target.kind === 'simple') {
    if (target.fieldKey === 'amount') {
      if (lead.type === 'visa') {
        const extra = syncVisaPricingExtra({ ...(lead.extra || {}) }, { sellTotal: value });
        return { amount: value, extra };
      }
      return { amount: value, quote_amount: lead.type === 'ticket' ? value : undefined };
    }
    if (lead.type === 'visa') {
      const extra = syncVisaPricingExtra({ ...(lead.extra || {}) }, { costTotal: value });
      return { cost_amount: value, extra };
    }
    if (lead.type === 'tour') {
      const extra = syncTourAgentCost({ ...(lead.extra || {}) }, value);
      return { cost_amount: value, extra };
    }
    return { cost_amount: value };
  }
  if (target.kind === 'adt_rate') {
    const extra = { ...(lead.extra || {}) };
    const costRates = { ...(extra.pax_cost_rates || {}) };
    const profitRates = { ...(extra.pax_profit_rates || {}) };
    if (fieldId === 'cost') costRates.adt = value;
    else profitRates.adt = value;
    extra.pax_cost_rates = costRates;
    extra.pax_profit_rates = profitRates;
    return { extra, recalcPricing: true };
  }
  const paxTarget = {
    kind: target.kind,
    index: target.index,
    id: `${target.kind}:${target.index}`,
    label: target.label,
  };
  return applyPassengerFieldEdit(lead, paxTarget, fieldId, value);
}

export function buildPricingHistoryNote(before, after, { target, fieldId, oldValue, newValue }) {
  const lines = ['Pricing updated:'];

  if (target?.kind === 'simple') {
    lines.push(`  ${target.label}: ${fmtAmt(oldValue)} → ${fmtAmt(newValue)}`);
  } else if (target && fieldId) {
    const fieldLabel = fieldId === 'cost' ? 'cost' : 'profit';
    lines.push(`  ${target.label} ${fieldLabel}: ${fmtAmt(oldValue)} → ${fmtAmt(newValue)}`);
  }

  if (before.cost !== after.cost || before.total !== after.total || before.profit !== after.profit) {
    lines.push(`  Total cost: ${fmtAmt(before.cost ?? 0)} → ${fmtAmt(after.cost ?? 0)}`);
    lines.push(`  Total profit: ${fmtAmt(before.profit ?? 0)} → ${fmtAmt(after.profit ?? 0)}`);
    lines.push(`  Quote total: ${fmtAmt(before.total ?? 0)} → ${fmtAmt(after.total ?? 0)}`);
  } else if (before.quote !== after.quote || before.cost !== after.cost) {
    lines.push(`  Quote: ${fmtAmt(before.quote ?? before.total ?? 0)} → ${fmtAmt(after.quote ?? after.total ?? 0)}`);
    lines.push(`  Cost: ${fmtAmt(before.cost ?? 0)} → ${fmtAmt(after.cost ?? 0)}`);
  }

  return lines.join('\n');
}

export function listPricingFieldChoices(target) {
  if (target.kind === 'simple') return [];
  return [
    { label: 'Net fare / cost', value: '1' },
    { label: 'Profit', value: '2' },
  ];
}
